import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useAtom, useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';

import { useAnalytics, useIsFreemiumUser } from '@restworld/utils-common';
import { useAuth } from '@restworld/feature-authentication';

import { useCheckInvoiceStatus } from './use-check-invoice-status';
import { invoiceStatusAtom } from '../employer-job-listing.atoms';
import { useShowOrganisationQuery } from '../../organisations/hooks/use-show-organisation-query';
import { isUnpaidInvoicesModalOpenAtom } from '../../job-position-onboarding/atoms/unpaid-invoices-modal.atoms';

export const NEW_JOB_POSITION_ROUTE = '/employer/job-positions/onboarding';
export const JOB_POSITIONS_ROUTE = '/employer/job-positions';

export type OpenNewJobPositionSource = 'homepage' | 'job_positions_page';

interface UseOpenNewJobPositionParams {
  source: OpenNewJobPositionSource;
  // The job positions page needs the invoice status on mount, because it decides there
  // whether to render InvoiceBlockPage. Secondary entry points check on click instead,
  // so that a frequently visited page does not pay an uncacheable request per visit.
  checkInvoicesOnMount?: boolean;
}

/**
 * Single implementation of the "open a new job position" action, shared by every entry
 * point. It is a client-side gate on the CTA, not an authorization guard: the wizard URL
 * stays reachable directly, and the payment step performs its own invoice check.
 */
export function useOpenNewJobPosition({ source, checkInvoicesOnMount = false }: UseOpenNewJobPositionParams) {
  const router = useRouter();
  const { t: translateCommon } = useTranslation('common');
  const { trackEvent } = useAnalytics();
  const { user } = useAuth();
  const isFreemiumUser = useIsFreemiumUser();
  const setIsUnpaidInvoicesModalOpen = useSetAtom(isUnpaidInvoicesModalOpenAtom);
  const [lastKnownInvoiceStatus, setLastKnownInvoiceStatus] = useAtom(invoiceStatusAtom);
  const [isCheckingOnClick, setIsCheckingOnClick] = useState(false);
  // The state above only stops a click that arrives after a rerender. Two clicks in the same
  // tick both read the same closure, so the lock against a double invoice check has to be
  // synchronous.
  const isOpeningRef = useRef(false);

  const isPpuUser = user?.using_ppu_business_model === true;

  const {
    data: invoiceStatusData,
    isChecking: isCheckingInvoices,
    refetch: refetchInvoiceStatus,
  } = useCheckInvoiceStatus({
    organizationId: user?.organization_id,
    enabled: checkInvoicesOnMount,
  });

  // The check is not cached, so a failed call would otherwise make an organization already
  // known as blocked look unblocked. The last successful answer survives for the surfaces
  // that render a blocked state, while the CTA below decides on the current answer only.
  useEffect(() => {
    if (invoiceStatusData) setLastKnownInvoiceStatus(invoiceStatusData);
  }, [invoiceStatusData, setLastKnownInvoiceStatus]);

  // The modal state is global and is also set from inside the wizard, so an entry point
  // mounting the modal must not inherit an open state left behind by another flow.
  useEffect(() => {
    setIsUnpaidInvoicesModalOpen(false);
  }, [setIsUnpaidInvoicesModalOpen]);

  const { organisationDetails, query: organisationQuery } = useShowOrganisationQuery({
    context: `employerApp:openNewJobPosition:${source}`,
    staleTime: 5 * 60 * 1000,
  });
  const isAtsOrganization = !!organisationDetails?.ats_referencenumber;
  // An unresolved organization must not open a window where an ATS organization can click
  // through, so the action stays busy until the query settles, and a failed organization
  // query withdraws the action from the secondary entry points rather than assuming non-ATS.
  const isResolvingOrganisation = organisationQuery.isLoading;
  const hasUnknownOrganisation = !organisationDetails && organisationQuery.isError;
  const isBusy = isResolvingOrganisation || isCheckingInvoices || isCheckingOnClick;

  const openNewJobPosition = useCallback(async () => {
    if (isOpeningRef.current || isBusy || isAtsOrganization || hasUnknownOrganisation || isFreemiumUser) return;
    isOpeningRef.current = true;

    try {
      trackEvent('employer_open_new_position', { email: user?.email, phone: user?.phone_number, source });

      // The pushes are awaited so the lock released below outlives the navigation: releasing
      // it while the route is still changing would let a second click start the flow again.
      if (!isPpuUser) {
        await router.push(NEW_JOB_POSITION_ROUTE);
        return;
      }

      // Deliberately the current answer, never the last known one: a stale "all paid" must not
      // open the wizard for an organization that stopped paying since.
      let status = invoiceStatusData;
      if (!checkInvoicesOnMount) {
        setIsCheckingOnClick(true);
        const result = await refetchInvoiceStatus();
        setIsCheckingOnClick(false);
        if (result.isError || !result.data) {
          // The check is the only thing standing between an unpaid organization and a new
          // billable position, so a failed check hands over to the job positions page, which
          // runs the same check on mount and owns the blocked experience.
          await router.push(JOB_POSITIONS_ROUTE);
          return;
        }
        status = result.data;
      }

      if (status?.all_paid && !status?.block_completely) await router.push(NEW_JOB_POSITION_ROUTE);
      else setIsUnpaidInvoicesModalOpen(true);
    } finally {
      isOpeningRef.current = false;
    }
  }, [
    isBusy,
    isAtsOrganization,
    hasUnknownOrganisation,
    isFreemiumUser,
    trackEvent,
    user?.email,
    user?.phone_number,
    source,
    isPpuUser,
    router,
    invoiceStatusData,
    checkInvoicesOnMount,
    refetchInvoiceStatus,
    setIsUnpaidInvoicesModalOpen,
  ]);

  return {
    openNewJobPosition,
    // Freemium users are steered to /employer/purchase by their own upgrade card, and an
    // organization that failed to resolve cannot be checked against the ATS rule, so both
    // lose the CTA entirely. An ATS organization keeps it, disabled with the tooltip that
    // explains why, the same treatment as the job positions page header: hiding the button
    // would leave the employer with no answer at all.
    isAvailable: !isFreemiumUser && !hasUnknownOrganisation,
    // An organization that failed to resolve also disables the CTA, but with no tooltip:
    // the job positions page renders the button from isDisabled alone, and without this it
    // would look clickable while every click is discarded.
    isDisabled: isAtsOrganization || hasUnknownOrganisation,
    disabledTooltip: isAtsOrganization ? translateCommon('ats_org_open_position_disabled_tooltip') : undefined,
    isBusy,
    isCheckingInvoices,
    // The last successful answer keeps a blocked organization blocked across a failed check.
    invoiceStatus: invoiceStatusData ?? lastKnownInvoiceStatus,
  };
}

export default useOpenNewJobPosition;
